import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertOctagon,
  Coffee,
  HandHelping,
  Info,
  LogOut,
  MessageSquarePlus,
  Pencil,
  Play,
  Plus,
  RotateCcw,
  ScanBarcode,
  Timer,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { PersonFace } from './WorkLockScreen';
import { WorkInstructionsPanel } from './WorkInstructionsPanel';
import { WorkDefectDialog } from './WorkDefectDialog';
import { WorkNoteDialog } from './WorkNoteDialog';
import { WorkBreakDialog } from './WorkBreakDialog';
import { WorkItemPicker } from './WorkItemPicker';
import { WorkSerialDialog } from './WorkSerialDialog';
import { WorkTicketDialog } from './WorkTicketDialog';
import { WorkTicketsSheet } from './WorkTicketsSheet';
import { WorkStationInfoSheet } from './WorkStationInfoSheet';
import { useWorkTickets } from '@/features/tickets/hooks';
import { isTicketClosed } from '@/features/tickets/model';
import {
  useCompleteUnit,
  useEndBreak,
  useEndSession,
  useSessionBreaks,
  useSessionHeartbeat,
  useSessionSnapshot,
  useSetSessionItem,
  useUndoUnit,
  useWorkInstructionFor,
} from '@/features/workMode/hooks';
import { useIdleTimer, useNow } from '@/features/workMode/kiosk';
import {
  describeWorkError,
  formatClock,
  formatDuration,
  secondsSince,
} from '@/features/workMode/api';
import { efficiency, paceTone } from '@/features/workMode/gamification';
import {
  BREAK_REASONS,
  type FloorWorkstation,
  type WorkInstructionStep,
  type WorkNoteKind,
  type WorkSession,
  type WorkShiftSummary,
} from '@/features/workMode/types';
import type { AvatarConfig } from '@/features/digitalOffice/avatars';

interface WorkBenchProps {
  session: WorkSession;
  station: FloorWorkstation;
  worker: { name: string; avatarUrl: string | null; config: AvatarConfig };
  itemName: string | null;
  /** The item carries a serial barcode: "+" asks for a scan, photos and lots. */
  serialized: boolean;
  onEnded: (summary: WorkShiftSummary) => void;
  /** The session was closed somewhere else (manager, another tablet). */
  onLost: () => void;
}

const IDLE_WARN_MS = 30 * 60_000;
const IDLE_GRACE_S = 90;

/**
 * The bench. One giant timer, one giant "+", and defect / note / break a
 * single tap away. On a wide screen the instructions sit beside the timer.
 * On a phone they are a second panel underneath, so the instructions (or the
 * "no instructions yet" message) cannot cover Scrap.
 */
export function WorkBench({
  session: initial,
  station,
  worker,
  itemName,
  serialized,
  onEnded,
  onLost,
}: WorkBenchProps) {
  const now = useNow(500);
  const { data: snap, error: snapError } = useSessionSnapshot(initial.id);
  const session = snap?.session ?? initial;
  const { data: breaks = [] } = useSessionBreaks(session.id);
  const { data: instruction, isLoading: instrLoading } = useWorkInstructionFor(
    session.item_id,
    station.id,
  );
  useSessionHeartbeat(session.id);

  const complete = useCompleteUnit();
  const undo = useUndoUnit();
  const endBreak = useEndBreak();
  const endSession = useEndSession();
  const setItem = useSetSessionItem();

  const [defectOpen, setDefectOpen] = useState(false);
  const [scrapOpen, setScrapOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteKind, setNoteKind] = useState<WorkNoteKind>('suggestion');
  const [noteStep, setNoteStep] = useState<WorkInstructionStep | null>(null);
  const [breakOpen, setBreakOpen] = useState(false);
  const [itemOpen, setItemOpen] = useState(false);
  const [serialOpen, setSerialOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestsOpen, setRequestsOpen] = useState(false);
  const [stationOpen, setStationOpen] = useState(false);
  const [pulse, setPulse] = useState(false);
  const [idleLeft, setIdleLeft] = useState<number | null>(null);

  // The worker's own tickets: a count on the "Requests" button, and a dot
  // when something changed since they last looked (per person, per tablet).
  const { data: myTickets = [] } = useWorkTickets(session.id);
  const seenKey = `atlas.work.tickets.seen:${session.user_id}`;
  const [seenAt, setSeenAt] = useState<string>(() => {
    try {
      return window.localStorage.getItem(seenKey) ?? '';
    } catch {
      return '';
    }
  });
  const markRequestsSeen = useCallback(() => {
    const stamp = new Date().toISOString();
    setSeenAt(stamp);
    try {
      window.localStorage.setItem(seenKey, stamp);
    } catch {
      /* private mode */
    }
  }, [seenKey]);
  const openRequests = myTickets.filter((t) => !isTicketClosed(t.status)).length;
  const requestUpdates = myTickets.filter((t) => t.last_activity_at > seenAt).length;

  // Session closed elsewhere -> back to the lock screen.
  useEffect(() => {
    if (!snapError) return;
    const msg = String((snapError as Error).message ?? '');
    if (msg.includes('work_session_ended') || msg.includes('work_session_not_found')) onLost();
  }, [snapError, onLost]);
  useEffect(() => {
    if (snap?.session.ended_at) onLost();
  }, [snap?.session.ended_at, onLost]);

  const openBreak = snap?.open_break ?? null;
  const anyDialog =
    defectOpen ||
    scrapOpen ||
    noteOpen ||
    breakOpen ||
    itemOpen ||
    serialOpen ||
    endOpen ||
    requestOpen ||
    requestsOpen ||
    stationOpen;

  // Net time on the current unit: wall clock minus breaks that overlap it.
  const unitSeconds = useMemo(() => {
    const start = Date.parse(session.unit_started_at);
    let total = (now - start) / 1000;
    for (const b of breaks) {
      const bs = Math.max(Date.parse(b.started_at), start);
      const be = b.ended_at ? Date.parse(b.ended_at) : now;
      if (be > bs) total -= (be - bs) / 1000;
    }
    return Math.max(0, total);
  }, [now, session.unit_started_at, breaks]);

  const shiftSeconds = secondsSince(session.started_at, now);
  const breakSeconds = openBreak ? secondsSince(openBreak.started_at, now) : 0;

  const target = instruction?.target_seconds ?? station.target_seconds ?? null;
  const eff = efficiency(unitSeconds > 0 ? unitSeconds : null, target);
  const tone = target
    ? unitSeconds <= target * 0.95
      ? 'ahead'
      : unitSeconds <= target * 1.15
        ? 'on'
        : 'behind'
    : paceTone(eff);

  const celebrate = useCallback(() => {
    setPulse(true);
    window.setTimeout(() => setPulse(false), 500);
    try {
      navigator.vibrate?.(30);
    } catch {
      /* not supported */
    }
  }, []);

  const doComplete = useCallback(async () => {
    if (complete.isPending || openBreak) return;
    if (serialized) {
      if (!session.item_id) {
        toast.error(describeWorkError('work_item_required'));
        return;
      }
      setSerialOpen(true);
      return;
    }
    try {
      const res = await complete.mutateAsync({ sessionId: session.id });
      celebrate();
      const u = res.unit;
      toast.success(
        `Unit #${res.session.units_completed} logged · ${formatDuration(u?.duration_seconds ?? 0)}`,
        { duration: 2500 },
      );
    } catch (e) {
      toast.error(describeWorkError(e));
    }
  }, [complete, openBreak, serialized, session.id, session.item_id, celebrate]);

  const doUndo = async () => {
    try {
      await undo.mutateAsync({ sessionId: session.id });
      toast.success('Last unit removed — the clock picked up where it was.');
    } catch (e) {
      toast.error(describeWorkError(e));
    }
  };

  const doResume = async () => {
    try {
      await endBreak.mutateAsync({ sessionId: session.id });
    } catch (e) {
      toast.error(describeWorkError(e));
    }
  };

  const doEnd = useCallback(
    async (reason: string) => {
      try {
        const summary = await endSession.mutateAsync({ sessionId: session.id, reason });
        onEnded(summary);
      } catch (e) {
        const msg = describeWorkError(e);
        if (msg.includes('already ended') || msg.includes('was closed')) onLost();
        else toast.error(msg);
      }
    },
    [endSession, session.id, onEnded, onLost],
  );

  // Idle: after 30 quiet minutes ask "still there?"; 90 s later sign out.
  useIdleTimer(idleLeft === null && !anyDialog, IDLE_WARN_MS, () => setIdleLeft(IDLE_GRACE_S));
  useEffect(() => {
    if (idleLeft === null) return;
    if (idleLeft <= 0) {
      void doEnd('idle');
      return;
    }
    const id = window.setTimeout(() => setIdleLeft((n) => (n === null ? null : n - 1)), 1000);
    return () => window.clearTimeout(id);
  }, [idleLeft, doEnd]);

  // Spacebar / Enter = "+" when nothing else has focus.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (anyDialog || idleLeft !== null) return;
      const el = document.activeElement as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable))
        return;
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        void doComplete();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [anyDialog, idleLeft, doComplete]);

  const steps = instruction?.steps ?? [];
  const first = worker.name.split(' ')[0] || worker.name;
  const breakLabel = openBreak
    ? (BREAK_REASONS.find((r) => r.value === openBreak.reason)?.label ?? 'Break')
    : null;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-border px-5 py-3">
        <div className="flex min-w-0 items-center gap-3">
          <PersonFace person={worker} size={44} />
          <div className="min-w-0">
            <p className="truncate text-base font-medium text-foreground">{worker.name}</p>
            <p className="truncate text-xs text-muted-foreground">{station.name}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setStationOpen(true)}
          className="flex min-h-[44px] items-center gap-2 rounded-xl border border-border bg-card px-3 py-1.5 text-left hover:bg-secondary"
          title="Photos, tools, parts and safety notes for this station"
        >
          <Info className="h-4 w-4 shrink-0 text-info" />
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Station</p>
            <p className="truncate text-sm font-medium text-foreground">
              {station.ppe.length > 0 ? `PPE: ${station.ppe.join(', ')}` : 'Setup & checklist'}
            </p>
          </div>
        </button>
        <button
          type="button"
          onClick={() => setItemOpen(true)}
          className="flex min-h-[44px] items-center gap-2 rounded-xl border border-border bg-card px-3 py-1.5 text-left hover:bg-secondary"
        >
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Building</p>
            <p className="truncate text-sm font-medium text-foreground">
              {itemName ?? 'Pick an item'}
            </p>
          </div>
          <Pencil className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
        <div className="ml-auto flex items-center gap-2">
          <div className="hidden text-right sm:block">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Shift</p>
            <p className="text-sm font-medium tabular-nums text-foreground">
              {formatClock(shiftSeconds)}
            </p>
          </div>
          <Button
            variant="outline"
            size="lg"
            className="relative"
            onClick={() => {
              markRequestsSeen();
              setRequestsOpen(true);
            }}
            aria-label={
              requestUpdates > 0
                ? `Requests, ${requestUpdates} updated`
                : `Requests, ${openRequests} open`
            }
          >
            <HandHelping className="h-5 w-5" />
            Requests
            {openRequests > 0 && (
              <span className="rounded-full bg-secondary px-1.5 text-xs tabular-nums text-muted-foreground">
                {openRequests}
              </span>
            )}
            {requestUpdates > 0 && (
              <span
                aria-hidden
                className="absolute -right-1 -top-1 h-3 w-3 rounded-full border-2 border-card bg-fresh-salmon"
              />
            )}
          </Button>
          <Button
            variant="outline"
            size="lg"
            onClick={() => setBreakOpen(true)}
            disabled={!!openBreak}
          >
            <Coffee className="h-5 w-5" />
            Break
          </Button>
          <Button variant="ghost" size="lg" onClick={() => setEndOpen(true)}>
            <LogOut className="h-5 w-5" />
            Sign out
          </Button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3 lg:grid lg:grid-cols-[minmax(360px,0.9fr)_1.1fr] lg:gap-4 lg:overflow-hidden lg:p-4">
        <section
          aria-label="Work mode"
          className="flex shrink-0 flex-col gap-2 max-lg:rounded-2xl max-lg:border max-lg:border-border max-lg:bg-card/40 max-lg:p-2 lg:min-h-0 lg:shrink lg:gap-4 lg:overflow-hidden"
        >
          <div
            className={cn(
              'flex flex-col rounded-2xl border transition-colors lg:min-h-0 lg:flex-1 lg:overflow-y-auto',
              openBreak
                ? 'border-atmosphere bg-atmosphere/20'
                : tone === 'behind'
                  ? 'border-fresh-salmon/70 bg-fresh-salmon/10'
                  : tone === 'ahead'
                    ? 'border-bright-moss bg-bright-moss/15'
                    : 'border-border bg-card',
            )}
          >
            <div className="flex w-full flex-col items-center px-4 py-4 text-center lg:my-auto lg:px-6 lg:py-6">
              {openBreak ? (
                <>
                  <p className="text-sm uppercase tracking-wide text-muted-foreground">
                    On break · {breakLabel}
                  </p>
                  <p className="mt-2 text-5xl font-light tabular-nums tracking-tight text-foreground sm:text-7xl">
                    {formatClock(breakSeconds)}
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    The unit clock is paused at {formatClock(unitSeconds)}.
                  </p>
                  <Button
                    size="lg"
                    className="mt-4 h-14 min-w-56 text-lg lg:mt-6 lg:h-16"
                    onClick={doResume}
                    disabled={endBreak.isPending}
                  >
                    <Play className="h-6 w-6" />
                    Resume
                  </Button>
                </>
              ) : (
                <>
                  <p className="flex items-center gap-2 text-sm uppercase tracking-wide text-muted-foreground">
                    <Timer className="h-4 w-4" />
                    Current unit · #{session.units_completed + session.units_scrapped + 1}
                  </p>
                  <p className="mt-2 text-6xl font-light tabular-nums tracking-tight text-foreground sm:text-7xl lg:text-8xl">
                    {formatClock(unitSeconds)}
                  </p>
                  <p
                    className={cn(
                      'mt-2 text-sm',
                      tone === 'behind'
                        ? 'text-fresh-salmon-dark'
                        : tone === 'ahead'
                          ? 'text-bright-moss-dark'
                          : 'text-muted-foreground',
                    )}
                  >
                    {target
                      ? `Target ${formatDuration(target)} · ${tone === 'ahead' ? 'ahead of pace' : tone === 'on' ? 'on pace' : 'over target'}`
                      : 'No target set for this build'}
                  </p>
                  <button
                    type="button"
                    onClick={doComplete}
                    disabled={complete.isPending}
                    className={cn(
                      'mt-4 flex h-16 w-full max-w-md items-center justify-center gap-3 rounded-3xl bg-primary text-2xl font-medium text-primary-foreground shadow-lg transition-transform lg:mt-6 lg:h-24 lg:text-3xl',
                      'hover:bg-old-growth focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring/40 active:scale-[0.98] disabled:opacity-60',
                      pulse && 'scale-[1.03] bg-bright-moss-dark',
                    )}
                    aria-label={serialized ? 'Unit done — scan serial' : 'Unit done'}
                  >
                    {serialized ? (
                      <ScanBarcode className="h-8 w-8 lg:h-10 lg:w-10" strokeWidth={2.25} />
                    ) : (
                      <Plus className="h-8 w-8 lg:h-10 lg:w-10" strokeWidth={2.5} />
                    )}
                    {serialized ? 'Done · scan serial' : 'Done'}
                  </button>
                  {serialized && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      Every unit of this item is logged by serial number, with photos and component lots.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="grid shrink-0 grid-cols-4 gap-2 rounded-2xl border border-border bg-card p-2 text-center lg:p-3">
            <Metric label="Today" value={String(snap?.units_today ?? session.units_completed)} />
            <Metric label="This shift" value={String(session.units_completed)} />
            <Metric
              label="Average"
              value={snap?.avg_seconds != null ? formatClock(snap.avg_seconds) : '—'}
            />
            <Metric
              label="Best"
              value={snap?.best_seconds != null ? formatClock(snap.best_seconds) : '—'}
            />
          </div>

          <div className="grid shrink-0 grid-cols-2 gap-2 sm:grid-cols-5">
            <Button
              variant="outline"
              size="lg"
              className="h-12 border-fresh-salmon/70 text-fresh-salmon-dark hover:bg-fresh-salmon/15 lg:h-14"
              onClick={() => setScrapOpen(true)}
            >
              <Trash2 className="h-5 w-5" />
              Scrap
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="h-12 lg:h-14"
              onClick={() => setDefectOpen(true)}
            >
              <AlertOctagon className="h-5 w-5" />
              Defect
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="h-12 lg:h-14"
              onClick={() => {
                setNoteKind('suggestion');
                setNoteStep(null);
                setNoteOpen(true);
              }}
            >
              <MessageSquarePlus className="h-5 w-5" />
              Note
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="h-12 lg:h-14"
              onClick={() => setRequestOpen(true)}
              title="Ask for a tool, a part, a repair, access — anything you need"
            >
              <HandHelping className="h-5 w-5" />
              Request
            </Button>
            <Button
              variant="ghost"
              size="lg"
              className="col-span-2 h-12 sm:col-span-1 lg:h-14"
              onClick={doUndo}
              disabled={
                undo.isPending ||
                !snap?.last_unit ||
                Date.parse(snap.last_unit.completed_at) < now - 5 * 60_000
              }
              title="Undo the last unit (within 5 minutes)"
            >
              <RotateCcw className="h-5 w-5" />
              Undo
            </Button>
          </div>
        </section>

        <WorkInstructionsPanel
          instruction={instruction}
          loading={instrLoading}
          onSuggestChange={(step) => {
            setNoteKind('instruction_change');
            setNoteStep(step);
            setNoteOpen(true);
          }}
          className="h-auto min-h-[15rem] shrink-0 grow lg:h-full lg:min-h-0 lg:grow-0"
        />
      </div>

      <WorkDefectDialog
        mode="scrap"
        open={scrapOpen}
        onOpenChange={setScrapOpen}
        session={session}
        steps={steps}
        committedSeconds={unitSeconds}
      />
      <WorkDefectDialog
        mode="defect"
        open={defectOpen}
        onOpenChange={setDefectOpen}
        session={session}
        steps={steps}
        committedSeconds={unitSeconds}
      />
      <WorkNoteDialog
        open={noteOpen}
        onOpenChange={setNoteOpen}
        session={session}
        initialKind={noteKind}
        step={noteStep}
        steps={steps}
      />
      <WorkBreakDialog open={breakOpen} onOpenChange={setBreakOpen} session={session} />
      <WorkSerialDialog
        open={serialOpen}
        onOpenChange={setSerialOpen}
        session={session}
        stationId={station.id}
        itemName={itemName}
        unitSeconds={unitSeconds}
        onLogged={(snap) => {
          celebrate();
          const su = snap.serialized_unit;
          toast.success(
            `${su?.serial_number ?? 'Unit'} logged · ${formatDuration(snap.unit?.duration_seconds ?? 0)}${su?.status === 'on_hold' ? ' · on hold' : ''}`,
            { duration: 3500 },
          );
        }}
      />
      <WorkTicketDialog
        open={requestOpen}
        onOpenChange={setRequestOpen}
        session={session}
        onSubmitted={markRequestsSeen}
      />
      <WorkTicketsSheet
        open={requestsOpen}
        onOpenChange={setRequestsOpen}
        session={session}
        onNewRequest={() => setRequestOpen(true)}
      />
      <WorkStationInfoSheet
        open={stationOpen}
        onOpenChange={setStationOpen}
        station={station}
        onRequest={() => {
          setStationOpen(false);
          setRequestOpen(true);
        }}
      />
      <WorkItemPicker
        open={itemOpen}
        onOpenChange={setItemOpen}
        stationId={station.id}
        currentItemId={session.item_id}
        allowNone
        onPick={async (id) => {
          if (id === session.item_id) return;
          try {
            await setItem.mutateAsync({ sessionId: session.id, itemId: id });
            toast.success('Switched item. The unit clock restarted.');
          } catch (e) {
            toast.error(describeWorkError(e));
          }
        }}
      />

      <Dialog open={endOpen} onOpenChange={setEndOpen}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle>Sign out, {first}?</DialogTitle>
            <DialogDescription>
              {session.units_completed} unit{session.units_completed === 1 ? '' : 's'} this shift.
              The unit on the bench right now will not be counted.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="ghost" size="lg" onClick={() => setEndOpen(false)}>
              Keep working
            </Button>
            <Button size="lg" onClick={() => doEnd('manual')} disabled={endSession.isPending}>
              {endSession.isPending ? 'Ending…' : 'End shift'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {idleLeft !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-humus-dark/70 p-6 backdrop-blur-sm"
          onPointerDown={() => setIdleLeft(null)}
        >
          <div className="w-full max-w-md rounded-2xl bg-card p-8 text-center shadow-xl">
            <p className="text-2xl font-medium text-foreground">Still there, {first}?</p>
            <p className="mt-2 text-muted-foreground">
              Nobody has touched the tablet for a while. Signing you out in {idleLeft}s.
            </p>
            <div className="mt-6 flex justify-center gap-3">
              <Button variant="outline" size="lg" onClick={() => void doEnd('idle')}>
                Sign me out
              </Button>
              <Button size="lg" onClick={() => setIdleLeft(null)}>
                I'm here
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-xl font-medium tabular-nums text-foreground">{value}</p>
    </div>
  );
}
